import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  BadgeColor,
  DesignAngularKitModule,
  ItSortEvent,
} from 'design-angular-kit';
import { FilterComponent } from '../../components/filter/filter.component';
import { RequestDto } from '../../models/request.dto';
import { AuthService } from '../../services/auth.service';
import { FindRequestDto } from '../../models/find-request.dto';
import { RequestService } from '../../services/request.service';
import { getBadgeType } from '../../models/request.util';
import { ReturnUrlService } from '../../services/return-url.service';
import { NgxPrintModule } from 'ngx-print';

@Component({
  selector: 'app-request-list',
  standalone: true,
  imports: [
    CommonModule,
    DesignAngularKitModule,
    FilterComponent,
    RouterModule,
    NgxPrintModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './request-list.component.html',
  styleUrl: './request-list.component.scss',
})
export class RequestListComponent implements OnInit {
  activeTab = 0;
  requests: RequestDto[] = [];
  total = 0;
  pageSize = 10;
  filters: FindRequestDto = { page: 0 };

  sortBy: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private requestService: RequestService,
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute,
    private returnUrlService: ReturnUrlService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params) {
        const codes = this.normalizeToArray(params['statusCodes']);
        const subsettingsString = this.normalizeToArray(params['subsettingIds']);
        const subsettingsNum = subsettingsString.map(Number);

        this.filters = {
          sortBy: this.sortBy,
          sortDirection: this.sortDirection,
          page: this.filters.page || 0,
          size: this.pageSize || 10,
          isTransitionUvm: params['isTransitionUvm'],
          isUvmFilled: params['isUvmFilled'],
          patientFiscalCode: params['patientFiscalCode'],
          patientName: params['name'],
          patientSurname: params['surname'],
          code: params['tansitionCode'],
          startDate: params['startDate'],
          endDate: params['endDate'],
          statusCodes: 
            codes.length > 0
              ? codes
              : [
                  'PIC',
                  'NEW',
                  'CP',
                  'CS',
                  'BOZ',
                  'DEL',
                  'END',
                  'CHI',
                  'APE',
                  'DAT',
                  'TPV',
                  'VAL',
                  'CPZ',
                  'CST',
                  'SOL1',
                  'SOL2',
                  'SOL3',
                  'TPA',
                  'SS'
                ],
          transitionFindOwnershipRequest: {
            ownershipUserCode: this.authService.userInfo.username,
            withPastUserOwnership: params['withPastUserOwnership'] || true,
            withCurrentUserOwnership:
              params['withCurrentUserOwnership'] || true,
          },
        };
        if(subsettingsNum?.length) {
          this.filters.subsettingIds = subsettingsNum;
        }
      }
    });

    this.loadRequests();
  }

  private normalizeToArray(value: string | string[] | undefined): string[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  loadRequests(): void {
    if(this.authService.isOperatoreSuperUser || this.authService.isOperatoreMmg) {
      this.filters.transitionFindOwnershipRequest = undefined;
    }
    this.requestService.getRequests(this.filters).subscribe((res) => {
      this.requests = res.transitions;
      this.total = res.total;
    });
  }

  getBadgeColor(code: string): BadgeColor {
    return getBadgeType(code);
  }

  getSubstatusesText(substatus: string): string {
    return this.requestService.getRequestStatusLabel(substatus);
  }

  onFiltersChanged(filters: any) {
    for (const key of Object.keys(filters)) {
      this.filters[key] = filters[key];
    }
    if(!this.authService.isOperatoreSuperUser && !this.authService.isOperatoreMmg) {
      this.filters.transitionFindOwnershipRequest = {
        ownershipUserCode: this.authService.userInfo.username,
        withCurrentUserOwnership: filters.withCurrentUserOwnership || false,
        withPastUserOwnership: filters.withPastUserOwnership || false,
      };
    }
    console.log(filters)
    console.log(filters.patientFiscalCode)
    this.filters.page = 0;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: filters.page || 0,
        size: filters.pageSize || 10,
        patientFiscalCode: filters.patientFiscalCode,
        name: filters.patientName,
        surname: filters.patientSurname,
        tansitionCode: filters.code,
        statusCodes: filters.statusCodes,
        isTransitionUvm: filters.isTransitionUvm,
        isUvmFilled: filters.isUvmFilled,
        withPastUserOwnership: filters.withPastUserOwnership,
        withCurrentUserOwnership: filters.withCurrentUserOwnership,
        startDate: filters.startDate,
        endDate: filters.endDate,
        subsettingIds: filters.subsettingIds
      },
      queryParamsHandling: 'merge',
    });

    this.loadRequests();
  }

  sortData(event: ItSortEvent): void {
    this.filters.sortBy = event.active;
    this.filters.sortDirection = event.direction ?? 'asc';
    this.loadRequests();
  }

  changerEvent(event: number) {
    this.filters.size = event;
    this.loadRequests();
  }

  onPageChange(event: any): void {
    this.filters.page = event;
    this.loadRequests();
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.filters.size!);
  }

  get startIndex(): number {
    return this.filters.page! * this.filters.size! + 1;
  }

  get endIndex(): number {
    return Math.min((this.filters.page! + 1) * this.filters.size!, this.total);
  }

  navigateToRequest(uuid: string) {
    const fullUrl = this.router.url;
    this.returnUrlService.set(fullUrl);
    this.router.navigate(['/detail/' + uuid]);
  }
}
